import { useState, useEffect, useCallback, useRef, type RefObject } from 'react';

interface ToolbarPositionResult {
  showToolbar: boolean;
  setShowToolbar: (value: boolean | ((prev: boolean) => boolean)) => void;
  closeToolbar: () => void;
  toolbarPosition: { top: number; left: number } | null;
  buttonRef: RefObject<HTMLButtonElement>;
  toolbarRef: RefObject<HTMLDivElement>;
}

const ESTIMATED_TOOLBAR_HEIGHT = 60;
const ESTIMATED_TOOLBAR_WIDTH = 200;

export default function useToolbarPosition(): ToolbarPositionResult {
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const closeToolbar = useCallback((): void => setShowToolbar(false), []);

  useEffect(() => {
    if (!showToolbar || !buttonRef.current) {
      setToolbarPosition(null);
      return;
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = buttonRect.bottom + 4;
    let left = buttonRect.left;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    if (spaceBelow < ESTIMATED_TOOLBAR_HEIGHT && buttonRect.top > spaceBelow) {
      top = buttonRect.top - ESTIMATED_TOOLBAR_HEIGHT - 4;
    }

    if (left + ESTIMATED_TOOLBAR_WIDTH > viewportWidth) {
      left = viewportWidth - ESTIMATED_TOOLBAR_WIDTH - 8;
    }

    setToolbarPosition({ top, left });

    const handleClickOutside = (event: MouseEvent): void => {
      if (
        buttonRef.current &&
        toolbarRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !toolbarRef.current.contains(event.target as Node)
      ) {
        setShowToolbar(false);
      }
    };

    const handleScroll = (): void => setShowToolbar(false);
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setShowToolbar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showToolbar]);

  return { showToolbar, setShowToolbar, closeToolbar, toolbarPosition, buttonRef, toolbarRef };
}
